import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Scale, FileText, CreditCard, Shield, AlertTriangle, Gavel, Mail, User } from 'lucide-react';

const TermsConditionsPage = () => {
  const sections = [
    {
      id: 'acceptance-of-terms',
      title: 'Acceptance of Terms',
      icon: <Scale className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Agreement to Terms',
          text: 'By creating an account or using any part of ThatAIHumanizer, you agree to these Terms and our Privacy Policy (if applicable). If you do not agree, do not use our services.'
        }
      ]
    },
    {
      id: 'description-of-service',
      title: 'Description of Service',
      icon: <FileText className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Service Overview',
          text: 'ThatAIHumanizer allows users to transform AI-generated content to sound more human. Users can buy word-based credits (1 credit = 1 word). New users receive 1,000 free credits upon registration.'
        }
      ]
    },
    {
      id: 'accounts-and-access',
      title: 'Accounts and Access',
      icon: <User className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Account Creation',
          text: 'You must create an account to use the service.'
        },
        {
          subtitle: 'Account Security',
          text: 'You are responsible for maintaining the confidentiality of your login credentials.'
        },
        {
          subtitle: 'Data Collection',
          text: 'We store your email address for the sole purpose of tracking your credit usage.'
        },
        {
          subtitle: 'Account Restrictions',
          text: 'You may not share your account or resell credits.'
        }
      ]
    },
    {
      id: 'payment-and-credits',
      title: 'Payment and Credits',
      icon: <CreditCard className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Credit Purchase',
          text: 'Credits can be purchased via a one-time payment system.'
        },
        {
          subtitle: 'Refund Policy',
          text: 'All payments are final and non-refundable, unless required by law.'
        },
        {
          subtitle: 'Credit Properties',
          text: 'Credits do not expire but are non-transferable and have no monetary value outside of the service.'
        }
      ]
    },
    {
      id: 'prohibited-use',
      title: 'Prohibited Use',
      icon: <AlertTriangle className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Prohibited Activities',
          text: 'You agree not to use ThatAIHumanizer for: Academic cheating or plagiarism, generating spam or deceptive content, promoting hate speech, violence, or harassment, bypassing AI safeguards (e.g., jailbreaks, prompt injections), or any other illegal or malicious activity.'
        },
        {
          subtitle: 'Account Suspension',
          text: 'We reserve the right to suspend or terminate your account without notice if we suspect misuse.'
        }
      ]
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      icon: <Shield className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Content Ownership',
          text: 'All content generated using ThatAIHumanizer remains yours. However, you grant us a limited license to process and deliver your content for the purpose of providing the service.'
        },
        {
          subtitle: 'Data Storage',
          text: 'We do not store your submitted text or generated outputs.'
        }
      ]
    },
    {
      id: 'disclaimer-of-warranty',
      title: 'Disclaimer of Warranty',
      icon: <AlertTriangle className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Service Disclaimer',
          text: 'ThatAIHumanizer is provided "as is" and "as available." We make no warranties regarding the accuracy, reliability, or effectiveness of outputs in bypassing AI detectors or being undetectable.'
        },
        {
          subtitle: 'User Discretion',
          text: 'Use the tool at your own discretion.'
        }
      ]
    },
    {
      id: 'limitation-of-liability',
      title: 'Limitation of Liability',
      icon: <Shield className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Liability Limitations',
          text: 'To the fullest extent permitted by law, ThatAIHumanizer shall not be liable for: Any loss of data, revenue, or business; Any misuse of the tool by end users; Any decisions made based on generated content.'
        }
      ]
    },
    {
      id: 'termination',
      title: 'Termination',
      icon: <AlertTriangle className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Account Termination',
          text: 'We reserve the right to terminate or suspend access to your account at our discretion, particularly in cases of rule violations or malicious use.'
        }
      ]
    },
    {
      id: 'jurisdiction',
      title: 'Jurisdiction',
      icon: <Gavel className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Governing Law',
          text: 'These Terms are governed by the laws of the Republic of the Philippines. Any disputes shall be resolved in the competent courts of the Philippines.'
        }
      ]
    },
    {
      id: 'changes-to-terms',
      title: 'Changes to Terms',
      icon: <FileText className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Updates',
          text: 'We may update these Terms occasionally. Any changes will be posted on this page with the new effective date. Continued use of the service after updates means you accept the revised Terms.'
        }
      ]
    },
    {
      id: 'contact-us',
      title: 'Contact Us',
      icon: <Mail className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Contact Information',
          text: 'If you have questions about these Terms, you may reach us at: 📧 thataihumanizer@gmail.com'
        }
      ]
    }
  ];

  const effectiveDate = "June 1, 2025";

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary/10 rounded-full">
                <Scale className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Terms and Conditions
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Welcome to ThatAIHumanizer. Please read these terms carefully before using our service. By accessing or using our website and services, you agree to be bound by these Terms and Conditions.
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" asChild>
                <Link to="/">Back to Home</Link>
              </Button>
              <Button asChild>
                <Link to="/app">Try Our Service</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Table of Contents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-2">
                {sections.map((section, index) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors text-sm text-muted-foreground hover:text-foreground"
                  >
                    {section.icon}
                    <span>{index + 1}. {section.title}</span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Introduction */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Introduction</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                <strong>Effective Date:</strong> {effectiveDate}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Welcome to ThatAIHumanizer (<a href="https://thataihumanizer.pages.dev/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://thataihumanizer.pages.dev/</a>), a service owned and operated from the Philippines. By accessing or using our website and services, you agree to be bound by the following Terms and Conditions.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Please read them carefully before using the service.
              </p>
            </CardContent>
          </Card>

          {/* Main Sections */}
          {sections.map((section, index) => (
            <Card key={section.id} id={section.id} className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  {section.icon}
                  {index + 1}. {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {section.content.map((item, itemIndex) => (
                  <div key={itemIndex} className="space-y-2">
                    {item.subtitle && (
                      <h4 className="font-semibold text-foreground">{item.subtitle}</h4>
                    )}
                    <p className="text-muted-foreground leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}

          {/* Footer */}
          <Card className="mt-12">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  Last updated: {effectiveDate}
                </p>
                <div className="flex justify-center gap-4">
                  <Button variant="outline" asChild>
                    <Link to="/privacy">Privacy Policy</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/">Back to Home</Link>
                  </Button>
                  <Button asChild>
                    <Link to="/app">Start Using Service</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsPage; 